import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveFolderProblem,
  nativeToVpath,
  vpathToNative,
  type ArchiveFolderProblem,
} from '@zerogap/shared';
import { ApiError, del, get, patch, post, qs } from '../api';
import { arrow, locale, msg, tr } from '../i18n';
import { useTenant } from '../session';
import { ShareDialog } from './shares';
import {
  ago, Badge, bytes, Button, Card, ConfirmDialog, Empty, ErrorBox, Field, friendlyError, Icons, Loading, Modal, PageHead, Segmented, Tabs, TechDetails,
  useToast, when,
} from '../ui';

/**
 * Folder archives (docs/folder-archives.md): a folder on a computer copied to the cloud, whose oldest files leave the computer
 * when it gets too old or too big. Files leave the computer only after their copy in the cloud has been
 * checked, and they can always be brought back.
 */

export interface Archive {
  id: string;
  deviceId: string;
  path: string;
  unit: 'file' | 'item';
  maxBytes: number | null;
  maxAgeDays: number | null;
  /** How long files stay in the cloud before they are deleted for good; null keeps them forever. */
  keepDays: number | null;
  status: 'active' | 'stopped';
  createdAt: string;
  stoppedAt: string | null;
  local: { files: number; bytes: number; checkedAt: string | null; folderMissing: boolean };
  cloud: { files: number; bytes: number; removed: number };
  lastRunAt: string | null;
  lastError: string | null;
}

type Style = 'windows' | 'posix';
const GB = 1024 ** 3;

export const folderName = (vpath: string, style: Style) => {
  const native = vpathToNative(vpath, style);
  return native.split(/[\\/]/).filter(Boolean).pop() ?? native;
};

export function limitsText(a: Pick<Archive, 'maxBytes' | 'maxAgeDays'>) {
  const age = a.maxAgeDays ? tr('older than {n} days', { n: a.maxAgeDays }) : null;
  const size = a.maxBytes ? tr('over {size}', { size: bytes(a.maxBytes) }) : null;
  if (age && size) return tr('Moves files {age}, or when the folder is {size}', { age, size });
  return tr('Moves files {limit}', { limit: (age ?? size)! });
}

type KeepUnit = 'days' | 'months' | 'years';
const UNIT_DAYS: Record<KeepUnit, number> = { days: 1, months: 30, years: 365 };

/** A number of days in the largest unit that divides it evenly (365 -> 1 year, 90 -> 3 months). */
function splitDays(days: number): { n: number; unit: KeepUnit } {
  if (days % 365 === 0) return { n: days / 365, unit: 'years' };
  if (days % 30 === 0) return { n: days / 30, unit: 'months' };
  return { n: days, unit: 'days' };
}

export function durationText(days: number): string {
  const { n, unit } = splitDays(days);
  if (unit === 'years') return n === 1 ? tr('1 year') : tr('{n} years', { n });
  if (unit === 'months') return n === 1 ? tr('1 month') : tr('{n} months', { n });
  return n === 1 ? tr('1 day') : tr('{n} days', { n });
}

export function useArchives(deviceId: string | undefined, enabled = true) {
  const t = useTenant();
  return useQuery({
    queryKey: ['archives', deviceId],
    queryFn: () => get<{ pathStyle: Style; supported: boolean; browseSupported: boolean; archives: Archive[] }>(`/api/t/${t.tenantId}/devices/${deviceId}/archives`),
    enabled: !!deviceId && enabled,
    refetchInterval: 30_000,
  });
}

// ───────── on the computer's page ─────────

/** The "Archives" part of a computer's page: its archive folders, and adding one. */
export function ArchivesSection({ deviceId, active }: { deviceId: string; active: boolean }) {
  const t = useTenant();
  const q = useArchives(deviceId);
  const [adding, setAdding] = useState(false);
  const qc = useQueryClient();
  if (q.isLoading) return <Loading rows={2} />;
  if (q.error) return <ErrorBox error={q.error} />;
  const { archives, pathStyle, supported, browseSupported } = q.data!;
  const canAdd = t.can('devices.manage') && t.can('devices.browse') && active;
  return (
    <div className="archives">
      <div className="row between" style={{ marginBottom: 6 }}>
        <h3 style={{ margin: 0 }}>{tr('Archives')}</h3>
        {canAdd && supported && browseSupported && <Button size="sm" onClick={() => setAdding(true)}><Icons.plus width={14} />{tr('Add archive')}</Button>}
      </div>
      {archives.length === 0 ? (
        <p className="small muted" style={{ margin: 0 }}>{tr('An archive keeps a folder in the cloud. Old files then leave this computer to free up space, and stay one click away.')}</p>
      ) : (
        <ul className="archive-list">
          {archives.map((a) => (
            <li key={a.id}>
              <Link to={`${t.base}/devices/${deviceId}/archives/${a.id}`} className="archive-row">
                <Icons.folder className="ficon dir" />
                <span className="archive-name"><bdi>{folderName(a.path, pathStyle)}</bdi></span>
                {a.status === 'stopped' && <Badge tone="neutral" dot={false}>{tr('Stopped')}</Badge>}
                {a.status === 'active' && a.local.folderMissing && <Badge tone="warn">{tr('Folder not found')}</Badge>}
              </Link>
              <div className="small muted archive-sub">
                {a.status === 'active' ? limitsText(a) : tr('No longer watched')}
                {a.cloud.files > 0 && <> · {tr('{size} in the cloud', { size: bytes(a.cloud.bytes) })}</>}
                {a.keepDays !== null && <> · {tr('kept for {time} after leaving the computer', { time: durationText(a.keepDays) })}</>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {!supported && canAdd && (
        <p className="small muted" style={{ marginBottom: 0 }}>{tr('To use archives, the ZeroGap app on this computer needs an update. It updates itself; this usually takes a day.')}</p>
      )}
      {adding && (
        <ArchiveDialog
          deviceId={deviceId}
          pathStyle={pathStyle}
          others={archives.filter((a) => a.status === 'active').map((a) => a.path)}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            qc.invalidateQueries({ queryKey: ['archives', deviceId] });
          }}
        />
      )}
    </div>
  );
}

// ───────── add / edit ─────────

export function ArchiveDialog({ deviceId, pathStyle, others, archive, onClose, onSaved }: {
  deviceId: string;
  pathStyle: Style;
  others: string[];
  archive?: Archive;
  onClose: () => void;
  onSaved: (a: Archive) => void;
}) {
  const t = useTenant();
  const toast = useToast();
  // A stopped archive moves nothing, but how long it keeps its files can still change.
  const keepOnly = archive?.status === 'stopped';
  const [path, setPath] = useState<string | null>(archive?.path ?? null);
  const [ageOn, setAgeOn] = useState(archive ? archive.maxAgeDays !== null : true);
  const [days, setDays] = useState(String(archive?.maxAgeDays ?? 90));
  const [sizeOn, setSizeOn] = useState(archive ? archive.maxBytes !== null : false);
  const [gb, setGb] = useState(String(archive?.maxBytes ? Math.round(archive.maxBytes / GB) : 200));
  const [unit, setUnit] = useState<'file' | 'item'>(archive?.unit ?? 'file');
  const initialKeep = archive?.keepDays ? splitDays(archive.keepDays) : { n: 7, unit: 'years' as KeepUnit };
  const [keepMode, setKeepMode] = useState<'forever' | 'limited'>(archive?.keepDays ? 'limited' : 'forever');
  const [keepN, setKeepN] = useState(String(initialKeep.n));
  const [keepUnit, setKeepUnit] = useState<KeepUnit>(initialKeep.unit);
  // Set when the server says the new setting deletes files at once; saving again confirms it.
  const [deleting, setDeleting] = useState<{ files: number; bytes: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dayN = Number(days);
  const gbN = Number(gb);
  const keepNum = Number(keepN);
  const keepDays = keepMode === 'forever' ? null : Math.round(keepNum * UNIT_DAYS[keepUnit]);
  const keepOk = keepMode === 'forever' || (Number.isInteger(keepNum) && keepNum >= 1 && keepDays! <= 36500);
  const limitsOk = keepOnly || ((ageOn || sizeOn) && (!ageOn || (Number.isInteger(dayN) && dayN >= 1 && dayN <= 36500)) && (!sizeOn || (Number.isFinite(gbN) && gbN >= 1)));
  const changeKeep = () => setDeleting(null);
  const save = async () => {
    setBusy(true);
    setErr(null);
    const body = keepOnly
      ? { keepDays }
      : { unit, maxAgeDays: ageOn ? dayN : null, maxBytes: sizeOn ? Math.round(gbN * GB) : null, keepDays };
    try {
      const r = archive
        ? await patch(`/api/t/${t.tenantId}/devices/${deviceId}/archives/${archive.id}`, { ...body, confirmDeletion: !!deleting })
        : await post(`/api/t/${t.tenantId}/devices/${deviceId}/archives`, { path, ...body });
      toast(archive ? tr('Archive saved') : tr('Archive added. The computer checks the folder within a few minutes.'));
      onSaved(r.archive);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'archive_keep_deletes_files') {
        const d = e.details as { files: number; bytes: number };
        setDeleting({ files: Number(d.files), bytes: Number(d.bytes) });
      } else setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      wide
      title={!archive ? tr('Add an archive') : keepOnly ? tr('How long the archive keeps files') : tr('Archive settings')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{tr('Cancel')}</Button>
          {deleting
            ? <Button variant="danger solid" busy={busy} onClick={save}>{tr('Delete them for good and save')}</Button>
            : <Button variant="primary" busy={busy} disabled={!path || !limitsOk || !keepOk} onClick={save}>{archive ? tr('Save') : tr('Add archive')}</Button>}
        </>
      }
    >
      <div className="stack">
        <Field label={tr('Which folder?')} hint={archive ? undefined : tr('Choose the folder to keep in the cloud, for example a folder with one subfolder per event.')}>
          {archive ? (
            <div className="mono" dir="ltr" style={{ textAlign: 'start' }}>{vpathToNative(archive.path, pathStyle)}</div>
          ) : (
            <FolderTreePicker deviceId={deviceId} pathStyle={pathStyle} others={others} value={path} onChange={setPath} />
          )}
        </Field>
        {!keepOnly && (
          <>
            <Field label={tr('When should files leave this computer?')} hint={tr('Everything in the folder is copied to the cloud right away and can be shared. These rules only decide when a file is removed from this computer to free up space. Choose one or both; the oldest files go first.')}>
              <div className="stack" style={{ gap: 8 }}>
                <label className="check">
                  <input type="checkbox" checked={ageOn} onChange={(e) => setAgeOn(e.target.checked)} />
                  <span className="row" style={{ gap: 6 }}>
                    {tr('When a file has not changed for')}
                    <input type="number" min={1} max={36500} value={days} disabled={!ageOn} onChange={(e) => setDays(e.target.value)} style={{ width: 90 }} aria-label={tr('Days')} />
                    {tr('days')}
                  </span>
                </label>
                <label className="check">
                  <input type="checkbox" checked={sizeOn} onChange={(e) => setSizeOn(e.target.checked)} />
                  <span className="row" style={{ gap: 6 }}>
                    {tr('When the folder is bigger than')}
                    <input type="number" min={1} value={gb} disabled={!sizeOn} onChange={(e) => setGb(e.target.value)} style={{ width: 90 }} aria-label={tr('Gigabytes')} />
                    GB
                  </span>
                </label>
                {!ageOn && !sizeOn && <div className="small" style={{ color: 'var(--bad)' }}>{tr('Choose at least one.')}</div>}
              </div>
            </Field>
            <Field label={tr('What leaves the computer together?')}>
              <Segmented value={unit} onChange={setUnit} options={[{ key: 'file', label: tr('Each file on its own') }, { key: 'item', label: tr('Whole folders inside it') }]} />
              <div className="small muted" style={{ marginTop: 6 }}>
                {unit === 'file'
                  ? tr('Every file is judged by its own date. Part of a folder can leave the computer while the rest stays.')
                  : tr('Each folder directly inside (for example, one per event) leaves the computer as a whole, once nothing in it has changed for the chosen time.')}
              </div>
            </Field>
          </>
        )}
        <Field label={tr('How long should the cloud keep them?')} hint={tr('Counted from the day each file moved to the cloud.')}>
          <Segmented value={keepMode} onChange={(v) => { setKeepMode(v); changeKeep(); }} options={[{ key: 'forever', label: tr('Forever') }, { key: 'limited', label: tr('For a set time') }]} />
          {keepMode === 'limited' && (
            <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'nowrap', alignItems: 'center' }}>
              <input type="number" min={1} value={keepN} onChange={(e) => { setKeepN(e.target.value); changeKeep(); }} style={{ width: 90 }} aria-label={tr('How long')} />
              <select style={{ width: 'auto' }} value={keepUnit} onChange={(e) => { setKeepUnit(e.target.value as KeepUnit); changeKeep(); }} aria-label={tr('Unit')}>
                <option value="days">{tr('days')}</option>
                <option value="months">{tr('months')}</option>
                <option value="years">{tr('years')}</option>
              </select>
              <span className="small muted">{tr('then deleted for good')}</span>
            </div>
          )}
          {keepMode === 'limited' && keepOk && (
            <div className="small" style={{ marginTop: 6, color: 'var(--warn)' }}>
              {tr('A file that is no longer on the computer is kept in the cloud for {time}, then deleted for good. Files still on the computer are always kept in the cloud.', { time: durationText(keepDays!) })}
            </div>
          )}
        </Field>
        {deleting && (
          <div className="notice bad">
            <strong>{tr('This deletes {files} files ({size}) for good', { files: deleting.files.toLocaleString(locale()), size: bytes(deleting.bytes) })}</strong>
            {tr('They have already been in the cloud longer than the new setting allows, so they are deleted within the hour. Files that are no longer on the computer cannot be brought back.')}
          </div>
        )}
        {!keepOnly && (
          <div className="notice ok">
            <strong>{tr('Nothing is lost by moving')}</strong>
            {keepMode === 'forever'
              ? tr('A file is removed from this computer only after its copy in the cloud has been read back and checked. It can be brought back at any time.')
              : tr('A file is removed from this computer only after its copy in the cloud has been read back and checked. It can be brought back until the cloud deletes it.')}
          </div>
        )}
        {err && <div className="notice bad">{err}</div>}
      </div>
    </Modal>
  );
}

// ───────── live folder picker ─────────

/** Whose folder a usual place is: the part after "Users" (Windows) or "home" (Linux). */
function profileOf(native: string): string | null {
  const parts = native.split(/[\\/]/).filter(Boolean);
  const i = parts.findIndex((p) => /^(users|home)$/i.test(p));
  return i >= 0 && parts[i + 1] ? parts[i + 1]! : null;
}

interface Folder {
  name: string;
  path: string;
  hasChildren: boolean;
  kind: 'drive' | 'place' | 'folder';
}
type NodeState = { state: 'loading' } | { state: 'ready'; folders: Folder[]; truncated: boolean } | { state: 'error'; error: string };

const PROBLEM_TEXT: Record<ArchiveFolderProblem, string> = {
  invalid: msg('This is not a folder on the computer'),
  network: msg('Folders on another computer or a network drive cannot be archived'),
  root: msg('A whole drive cannot be archived; choose a folder inside it'),
  system: msg('Windows and program folders cannot be archived'),
  profile_root: msg("A person's whole user folder cannot be archived; choose a folder inside it, such as Desktop or Pictures"),
  app_data: msg("ZeroGap's own folder cannot be archived"),
  inside_other: msg('This folder is inside another archive on this computer'),
  contains_other: msg('This folder contains another archive on this computer'),
};

/**
 * Lets a person open the computer's folders one level at a time and choose one. The computer answers
 * live (it must be on); only folder names travel, never file names.
 */
export function FolderTreePicker({ deviceId, pathStyle, others, value, onChange }: {
  deviceId: string;
  pathStyle: Style;
  others: string[];
  value: string | null;
  onChange: (vpath: string | null) => void;
}) {
  const t = useTenant();
  const [session, setSession] = useState<string | null>(null);
  const [startErr, setStartErr] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [nodes, setNodes] = useState<Record<string, NodeState>>({});
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [offline, setOffline] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    let sid: string | null = null;
    setNodes({});
    setOffline(false);
    setStartErr(null);
    post(`/api/t/${t.tenantId}/devices/${deviceId}/folder-browse`)
      .then((r) => {
        sid = r.sessionId;
        if (alive.current) setSession(r.sessionId);
      })
      .catch((e) => alive.current && setStartErr(friendlyError(e)));
    return () => {
      alive.current = false;
      if (sid) del(`/api/t/${t.tenantId}/devices/${deviceId}/folder-browse/${sid}`).catch(() => undefined);
    };
  }, [deviceId, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async (path: string) => {
    if (!session) return;
    setNodes((n) => ({ ...n, [path]: { state: 'loading' } }));
    const deadline = Date.now() + 75_000;
    let requestId: number | undefined;
    try {
      for (;;) {
        const r = await get(`/api/t/${t.tenantId}/devices/${deviceId}/folder-browse/${session}/list${qs({ path, requestId })}`);
        if (!alive.current) return;
        if (r.state === 'ready') {
          setNodes((n) => ({ ...n, [path]: { state: 'ready', folders: r.folders, truncated: r.truncated } }));
          setOffline(false);
          return;
        }
        if (r.state === 'error') {
          setNodes((n) => ({ ...n, [path]: { state: 'error', error: r.error } }));
          return;
        }
        requestId = r.requestId;
        if (Date.now() > deadline) {
          setOffline(true);
          setNodes((n) => ({ ...n, [path]: { state: 'error', error: tr('The computer did not answer.') } }));
          return;
        }
      }
    } catch (e) {
      if (alive.current) setNodes((n) => ({ ...n, [path]: { state: 'error', error: friendlyError(e) } }));
    }
  };
  useEffect(() => {
    if (session) void load('');
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (f: Folder, only?: 'open') => {
    const n = new Set(open);
    if (n.has(f.path)) {
      if (only === 'open') return;
      n.delete(f.path);
    } else {
      n.add(f.path);
      if (!nodes[f.path] || nodes[f.path]!.state === 'error') void load(f.path);
    }
    setOpen(n);
  };
  // Clicking a name chooses the folder (when it can be archived) and opens it, as in a file manager.
  const choose = (f: Folder, problem: ArchiveFolderProblem | null) => {
    if (!problem) onChange(nativeToVpath(f.path, pathStyle));
    if (f.hasChildren) toggle(f, 'open');
  };
  const problemOf = (native: string): ArchiveFolderProblem | null => {
    const v = nativeToVpath(native, pathStyle);
    return v ? archiveFolderProblem(v, pathStyle, others) : 'invalid';
  };
  const chosenNative = value ? vpathToNative(value, pathStyle) : null;

  const row = (f: Folder, depth: number): React.ReactNode => {
    const problem = problemOf(f.path);
    const isOpen = open.has(f.path);
    const node = nodes[f.path];
    const chosen = chosenNative !== null && chosenNative.toLowerCase() === f.path.toLowerCase();
    const owner = f.kind === 'place' ? profileOf(f.path) : null;
    return (
      <li key={f.path}>
        <div className={`tree-row ${chosen ? 'on' : ''} ${problem ? 'off' : ''}`} style={{ paddingInlineStart: 6 + depth * 18 }}>
          <button type="button" className="tree-twist" aria-label={isOpen ? tr('Close folder') : tr('Open folder')} aria-expanded={isOpen} disabled={!f.hasChildren} onClick={() => toggle(f)}>
            {f.hasChildren ? (isOpen ? '▾' : arrow.crumb()) : ''}
          </button>
          <button
            type="button"
            className="tree-name"
            title={problem ? tr(PROBLEM_TEXT[problem]) : undefined}
            onClick={() => choose(f, problem)}
          >
            <Icons.folder className="ficon dir" />
            <bdi>{f.kind === 'drive' ? f.path : f.name}</bdi>
            {owner && <span className="small muted"> · <bdi>{owner}</bdi></span>}
          </button>
        </div>
        {isOpen && (
          <ul className="tree">
            {!node || node.state === 'loading' ? (
              <li className="small muted" style={{ paddingInlineStart: 30 + depth * 18 }}>{tr('Opening…')}</li>
            ) : node.state === 'error' ? (
              <li className="small" style={{ paddingInlineStart: 30 + depth * 18, color: 'var(--bad)' }}>{node.error}</li>
            ) : node.folders.length === 0 ? (
              <li className="small muted" style={{ paddingInlineStart: 30 + depth * 18 }}>{tr('No folders inside')}</li>
            ) : (
              <>
                {node.folders.map((c) => row(c, depth + 1))}
                {node.truncated && <li className="small muted" style={{ paddingInlineStart: 30 + depth * 18 }}>{tr('Only the first 2,000 folders are shown')}</li>}
              </>
            )}
          </ul>
        )}
      </li>
    );
  };

  if (startErr) return <div className="notice warn">{startErr}</div>;
  const top = nodes[''];
  const places = top?.state === 'ready' ? top.folders.filter((f) => f.kind === 'place') : [];
  const drives = top?.state === 'ready' ? top.folders.filter((f) => f.kind !== 'place') : [];
  const chosenProblem = chosenNative ? problemOf(chosenNative) : null;
  return (
    <div className="folder-picker">
      {!top || top.state === 'loading' ? (
        <div className="row small muted" style={{ padding: 12 }}><span className="spin" aria-hidden />{tr('Connecting to the computer…')}</div>
      ) : top.state === 'error' ? (
        <div className="notice warn" style={{ margin: 10 }}>
          <strong>{offline ? tr('The computer is not answering') : tr('The folders could not be listed')}</strong>
          {offline ? tr('To choose a folder, the computer must be on and connected to the internet.') : top.error}
          <div style={{ marginTop: 8 }}><Button size="sm" onClick={() => setAttempt(attempt + 1)}>{tr('Try again')}</Button></div>
        </div>
      ) : (
        <div className="tree-wrap">
          {places.length > 0 && (
            <>
              <div className="tree-head small muted">{tr('Usual places')}</div>
              <ul className="tree">{places.map((f) => row(f, 0))}</ul>
            </>
          )}
          <div className="tree-head small muted">{tr('Drives')}</div>
          <ul className="tree">{drives.map((f) => row(f, 0))}</ul>
        </div>
      )}
      <div className="picker-foot small">
        {chosenNative ? (
          <>
            {tr('Chosen:')} <span className="mono" dir="ltr">{chosenNative}</span>
            {chosenProblem && <div style={{ color: 'var(--bad)' }}>{tr(PROBLEM_TEXT[chosenProblem])}</div>}
          </>
        ) : (
          <span className="muted">{tr('Open the folders and click the one to archive.')}</span>
        )}
      </div>
    </div>
  );
}

// ───────── one archive ─────────

const RUN_STATE: Record<string, [string, 'ok' | 'warn' | 'bad' | 'info' | 'neutral']> = {
  uploading: [msg('Sending to the cloud'), 'info'],
  committed: [msg('Checking the cloud copy'), 'info'],
  verified: [msg('Freeing up space'), 'info'],
  done: [msg('Done'), 'ok'],
  failed: [msg("Didn't finish, nothing was removed"), 'warn'],
};

export function ArchivePage() {
  const t = useTenant();
  const { deviceId, archiveId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<'files' | 'history' | 'kept'>('files');
  const [editing, setEditing] = useState(false);
  const [sharePaths, setSharePaths] = useState<string[] | null>(null);
  const [stopping, setStopping] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const d = useQuery({ queryKey: ['archive', archiveId], queryFn: () => get(`/api/t/${t.tenantId}/devices/${deviceId}/archives/${archiveId}`), refetchInterval: 20_000 });
  const runs = useQuery({ queryKey: ['archive-runs', archiveId], queryFn: () => get(`/api/t/${t.tenantId}/devices/${deviceId}/archives/${archiveId}/runs`), enabled: tab === 'history', refetchInterval: 20_000 });
  const others = useArchives(deviceId);
  if (d.isLoading) return <Loading rows={8} />;
  if (d.error) return <ErrorBox error={d.error} />;
  const a: Archive = d.data.archive;
  const style: Style = d.data.device.pathStyle;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['archive', archiveId] });
    qc.invalidateQueries({ queryKey: ['archives', deviceId] });
  };
  const toggle = (p: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(p)) n.delete(p);
      else n.add(p);
      for (const x of [...n]) if (x !== p && x.startsWith(p + '/')) n.delete(x);
      return n;
    });
  const getBack = (paths: string[]) => nav(`${t.base}/restore?device=${deviceId}&source=archive&archive=${a.id}`, { state: { paths } });
  const over = a.maxBytes !== null && a.local.bytes > a.maxBytes;
  return (
    <div>
      <PageHead
        eyebrow={<Link to={`${t.base}/devices/${deviceId}`}>{arrow.back()} {d.data.device.name}</Link>}
        title={<>{tr('Archive')}: <bdi>{folderName(a.path, style)}</bdi></>}
        desc={<span className="mono" dir="ltr">{vpathToNative(a.path, style)}</span>}
        actions={
          <>
            {t.can('restores.create') && a.cloud.files > 0 && <Button variant="primary" onClick={() => getBack([a.path])}>{tr('Get files back')}</Button>}
            {t.can('shares.create') && a.cloud.files > 0 && <Button onClick={() => setSharePaths([a.path])}><Icons.share />{tr('Share')}</Button>}
            {t.can('devices.manage') && <Button onClick={() => setEditing(true)}>{a.status === 'active' ? tr('Settings') : tr('How long it keeps files')}</Button>}
            {t.can('devices.manage') && a.status === 'active' && <Button variant="danger" onClick={() => setStopping(true)}>{tr('Stop archiving')}</Button>}
          </>
        }
      />
      <div className="grid g2">
        <Card title={tr('On this computer')}>
          {a.status === 'stopped' ? (
            <p className="muted" style={{ margin: 0 }}>{tr('This archive was stopped {time}. The folder is no longer watched; what is in the cloud stays there and can be brought back.', { time: ago(a.stoppedAt) })}</p>
          ) : (
            <>
              <div style={{ marginBottom: 10 }}>
                {a.local.folderMissing ? <Badge tone="warn">{tr('Folder not found on the computer')}</Badge>
                  : over ? <Badge tone="info">{tr('Over the limit: freeing up space')}</Badge>
                  : <Badge tone="ok">{tr('Watching the folder')}</Badge>}
              </div>
              <dl className="kv">
                <dt>{tr('Rule')}</dt><dd>{limitsText(a)}</dd>
                <dt>{tr('Leaves the computer')}</dt><dd>{a.unit === 'file' ? tr('Each file on its own') : tr('Whole folders inside it')}</dd>
                <dt>{tr('Still on the computer')}</dt><dd className="num">{tr('{files} files · {size}', { files: a.local.files.toLocaleString(locale()), size: bytes(a.local.bytes) })}{a.maxBytes ? <span className="muted"> / {bytes(a.maxBytes)}</span> : null}</dd>
                <dt>{tr('Last checked')}</dt><dd>{a.local.checkedAt ? ago(a.local.checkedAt) : tr('Not yet')}</dd>
              </dl>
              {a.lastError && <div className="notice warn" style={{ marginTop: 10 }}><strong>{tr('Last time something went wrong')}</strong>{tr('No file was removed. The computer tries again on its next check.')}<TechDetails summary={tr('Why?')}><span className="mono small" dir="ltr">{a.lastError}</span></TechDetails></div>}
            </>
          )}
        </Card>
        <Card title={tr('In the cloud')}>
          <dl className="kv">
            <dt>{tr('Files')}</dt><dd className="num">{a.cloud.files.toLocaleString(locale())}</dd>
            <dt>{tr('Size')}</dt><dd className="num">{bytes(a.cloud.bytes)}</dd>
            <dt>{tr('Space freed on the computer')}</dt><dd className="num">{tr('{n} files', { n: a.cloud.removed.toLocaleString(locale()) })}</dd>
            <dt>{tr('Last copied')}</dt><dd>{a.lastRunAt ? when(a.lastRunAt) : tr('Not yet')}</dd>
            <dt>{tr('Kept in the cloud')}</dt><dd>{a.keepDays === null ? tr('Forever') : tr('{time} after leaving the computer, then deleted for good', { time: durationText(a.keepDays) })}</dd>
          </dl>
          <p className="small muted" style={{ marginBottom: 0 }}>{tr('Every file was read back from the cloud and checked before it left the computer.')}</p>
          {a.status === 'active' && !d.data.device.copiesAll && (
            <p className="small muted" style={{ marginBottom: 0 }}>{tr('Until the ZeroGap app on this computer updates itself, files are copied to the cloud only when they leave the computer.')}</p>
          )}
        </Card>
      </div>
      <div style={{ marginTop: 20 }}>
        <Tabs value={tab} onChange={setTab} tabs={[
          { key: 'files', label: tr('Files') },
          { key: 'history', label: tr('History') },
          { key: 'kept', label: d.data.holds.length ? tr('Kept on the computer ({n})', { n: d.data.holds.length }) : tr('Kept on the computer') },
        ]} />
      </div>
      {tab === 'files' && (
        <div className="stack">
          <ArchiveBrowser deviceId={deviceId!} archive={a} pathStyle={style} selectable={t.can('restores.create')} selected={selected} onToggle={toggle} />
          {selected.size > 0 && (
            <div className="notice row between">
              <span><strong>{selected.size === 1 ? tr('1 item chosen') : tr('{n} items chosen', { n: selected.size })}</strong></span>
              <span className="row">
                {t.can('shares.create') && <Button onClick={() => setSharePaths([...selected])}><Icons.share />{tr('Share')}</Button>}
                <Button variant="primary" onClick={() => getBack([...selected])}>{tr('Get the chosen files back')}</Button>
              </span>
            </div>
          )}
        </div>
      )}
      {tab === 'history' && (
        <div className="card table-wrap">
          {runs.isLoading ? <div className="card-pad"><Loading /></div> : !runs.data?.runs.length ? <Empty glyph="◷" title={tr('Nothing has been copied yet')} /> : (
            <table>
              <thead><tr><th>{tr('When')}</th><th>{tr('Result')}</th><th className="right">{tr('Copied to the cloud')}</th><th className="right">{tr('Removed from the computer')}</th></tr></thead>
              <tbody>
                {runs.data.runs.map((r: any) => {
                  const [label, tone] = r.expired_at ? [msg('Deleted for good'), 'neutral' as const]
                    : r.state === 'done' && !Number(r.deleted_files) ? [msg('Copied to the cloud'), 'ok' as const]
                    : (RUN_STATE[r.state] ?? [r.state, 'neutral']);
                  return (
                    <tr key={r.id}>
                      <td className="small">{when(r.created_at)}</td>
                      <td><Badge tone={tone}>{tr(label)}</Badge>{r.expired_at && <div className="small muted">{tr('{files} files deleted for good {time}', { files: Number(r.expired_files ?? 0).toLocaleString(locale()), time: ago(r.expired_at) })}</div>}{r.error && !r.expired_at && <TechDetails inline summary={tr('Why?')}><span className="mono small" dir="ltr">{r.error}</span></TechDetails>}</td>
                      <td className="right num small">{tr('{files} files · {size}', { files: Number(r.files).toLocaleString(locale()), size: bytes(r.bytes) })}</td>
                      <td className="right num small">{tr('{files} files · {size}', { files: Number(r.deleted_files).toLocaleString(locale()), size: bytes(r.deleted_bytes) })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      {tab === 'kept' && (
        <Card>
          <p className="small muted" style={{ marginTop: 0 }}>{tr('Files brought back to where they were stay on the computer for the time chosen, even if they are older than the rule.')}</p>
          {d.data.holds.length === 0 ? <Empty glyph="·" title={tr('Nothing is being kept')} /> : (
            <table>
              <tbody>
                {d.data.holds.map((h: any) => (
                  <tr key={h.id}>
                    <td className="mono small" dir="ltr" style={{ textAlign: 'start' }}>{vpathToNative(h.path, style)}</td>
                    <td className="small">{h.until ? tr('Until {time}', { time: when(h.until) }) : tr('Until released')}</td>
                    <td className="right">
                      {t.can('devices.manage') && <Button size="sm" variant="ghost" onClick={async () => {
                        try {
                          await del(`/api/t/${t.tenantId}/devices/${deviceId}/archives/${a.id}/holds/${h.id}`);
                          refresh();
                        } catch (e) {
                          toast(friendlyError(e), true);
                        }
                      }}>{tr('Let it move again')}</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
      {sharePaths && <ShareDialog source={{ folderArchiveId: a.id }} paths={sharePaths} onClose={() => setSharePaths(null)} />}
      {editing && (
        <ArchiveDialog
          deviceId={deviceId!}
          pathStyle={style}
          archive={a}
          others={(others.data?.archives ?? []).filter((x) => x.status === 'active' && x.id !== a.id).map((x) => x.path)}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); refresh(); }}
        />
      )}
      {stopping && (
        <ConfirmDialog
          title={tr('Stop archiving this folder?')}
          confirm={tr('Stop archiving')}
          danger
          onClose={() => setStopping(false)}
          onConfirm={async () => {
            await post(`/api/t/${t.tenantId}/devices/${deviceId}/archives/${a.id}/stop`);
            setStopping(false);
            toast(tr('Archiving stopped'));
            refresh();
          }}
        >
          <p style={{ margin: 0 }}>{tr('The computer stops moving files from this folder. Nothing on the computer changes.')}</p>
          <p style={{ margin: 0 }}>{tr('Files already in the cloud stay there, and you can still bring them back from this page.')}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

// ───────── browsing an archive ─────────

interface Listing {
  path: string;
  name: string;
  kind: string;
  where: 'local' | 'cloud' | 'both';
  size: number;
  mtime: string | null;
  ageFrom?: string | null;
  archivedAt: string | null;
  leftAt: string | null;
}

const WHERE: Record<Listing['where'], [string, 'neutral' | 'info' | 'ok']> = {
  local: [msg('On the computer'), 'neutral'],
  cloud: [msg('In the cloud'), 'info'],
  both: [msg('On the computer and in the cloud'), 'ok'],
};

const dateOnly = (iso: string) => new Date(iso).toLocaleDateString(locale(), { day: 'numeric', month: 'long', year: 'numeric' });

function movesIn(e: Listing, a: Archive): string | null {
  if (a.status !== 'active' || !a.maxAgeDays || e.where === 'cloud' || !e.ageFrom) return null;
  const days = Math.ceil(a.maxAgeDays - (Date.now() - Date.parse(e.ageFrom)) / 86_400_000);
  if (days <= 0) return tr('leaves the computer soon');
  return days === 1 ? tr('leaves the computer in about a day') : tr('leaves the computer in about {n} days', { n: days });
}

/** One folder level of an archive, files on the computer and in the cloud side by side. */
export function ArchiveBrowser({ deviceId, archive, pathStyle, selectable, selected, onToggle }: {
  deviceId: string;
  archive: Archive;
  pathStyle: Style;
  selectable?: boolean;
  selected?: Set<string>;
  onToggle?: (path: string) => void;
}) {
  const t = useTenant();
  const root = archive.path;
  const [path, setPath] = useState(root);
  const [search, setSearch] = useState('');
  const base = `/api/t/${t.tenantId}/devices/${deviceId}/archives/${archive.id}`;
  const page = useInfiniteQuery({
    queryKey: ['archive-browse', archive.id, path],
    queryFn: ({ pageParam }) => get(`${base}/browse${qs({ path, offset: pageParam ?? 0, limit: 200 })}`),
    initialPageParam: 0 as number,
    getNextPageParam: (last: { entries: Listing[]; next: number | null }) => last.next ?? undefined,
  });
  const found = useQuery({ queryKey: ['archive-search', archive.id, search], queryFn: () => get(`${base}/search${qs({ q: search })}`), enabled: search.trim().length >= 2 });
  const rows: Listing[] = search.trim().length >= 2 ? (found.data?.results ?? []) : (page.data?.pages.flatMap((p) => p.entries) ?? []);
  const crumbs = useMemo(() => (path === root ? [] : path.slice(root.length + 1).split('/')), [path, root]);
  const isSel = (p: string) => !!selected && [...selected].some((s) => p === s || p.startsWith(s + '/'));
  return (
    <div className="card">
      <div className="card-head">
        <div className="crumbs" aria-label={tr('Location')}>
          <button onClick={() => { setPath(root); setSearch(''); }}><bdi>{folderName(root, pathStyle)}</bdi></button>
          {crumbs.map((c, i) => (
            <span key={i} className="row" style={{ gap: 4 }}>
              <span className="muted">{arrow.crumb()}</span>
              <button onClick={() => { setPath(root + '/' + crumbs.slice(0, i + 1).join('/')); setSearch(''); }}><bdi>{c}</bdi></button>
            </span>
          ))}
        </div>
        <input type="search" placeholder={tr('Search by file name…')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 240 }} aria-label={tr('Search this archive')} />
      </div>
      {page.error ? <div className="card-pad"><ErrorBox error={page.error} /></div> : (
        <div className="table-wrap" style={{ maxHeight: 520, overflow: 'auto' }}>
          <table>
            <thead><tr>{selectable && <th style={{ width: 36 }} />}<th>{tr('Name')}</th><th>{tr('Where')}</th><th className="right">{tr('Size')}</th><th>{tr('Last changed')}</th></tr></thead>
            <tbody>
              {rows.map((e) => {
                const [label, tone] = WHERE[e.where];
                const later = movesIn(e, archive);
                // The keep time counts from when the cloud copy stopped being the file on the computer.
                const gone = e.where !== 'local' && archive.keepDays && e.leftAt
                  ? new Date(Date.parse(e.leftAt) + archive.keepDays * 86_400_000).toISOString()
                  : null;
                return (
                  <tr key={e.path} className="fb-row">
                    {selectable && (
                      <td>
                        {e.where !== 'local' && (
                          <input type="checkbox" aria-label={tr('Select {name}', { name: e.name })} checked={isSel(e.path)} disabled={isSel(e.path) && !selected!.has(e.path)} onChange={() => onToggle?.(e.path)} />
                        )}
                      </td>
                    )}
                    <td>
                      <div className="fname">
                        {e.kind === 'd' ? <Icons.folder className="ficon dir" /> : <Icons.file className="ficon" />}
                        {e.kind === 'd' ? (
                          <button className="btn ghost sm" style={{ padding: '2px 4px' }} onClick={() => { setPath(e.path); setSearch(''); }}><bdi>{e.name}</bdi></button>
                        ) : (
                          <bdi>{search ? vpathToNative(e.path, pathStyle) : e.name}</bdi>
                        )}
                      </div>
                      {later && <div className="small muted">{later}</div>}
                      {gone && <div className="small muted">{tr('deleted from the cloud for good on {date}', { date: dateOnly(gone) })}</div>}
                    </td>
                    <td><Badge tone={tone} dot={false}>{tr(label)}</Badge></td>
                    <td className="right num small">{e.kind === 'd' ? '' : bytes(e.size)}</td>
                    <td className="small">{e.mtime ? when(e.mtime) : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(page.isLoading || found.isLoading) && <div className="card-pad"><Loading rows={3} /></div>}
          {!page.isLoading && rows.length === 0 && <Empty glyph="∅" title={search ? tr('No matches') : tr('This folder is empty')} />}
          {!search && page.hasNextPage && <div className="card-pad"><Button onClick={() => page.fetchNextPage()} busy={page.isFetchingNextPage}>{tr('Show more')}</Button></div>}
        </div>
      )}
    </div>
  );
}

